import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight } from "lucide-react";

const STEPS = [
  ["Idea", "It starts as a conversation — a sketch, a sentence, a 'what if'. That's enough."],
  ["Strategy", "Positioning, audience and messaging decided on purpose, before anything gets designed."],
  ["Identity", "Name direction, voice, colors and typography — the personality of the brand."],
  ["Logo", "The mark everything else hangs on, built as a system, not a single file."],
  ["Website", "Designed and built to convert — your hardest-working employee."],
  ["Content", "Photography, video and social assets that make the brand visible everywhere."],
  ["Launch", "Campaigns and rollout that put the brand in front of real people."],
  ["Growth", "Monthly content and marketing that keep the momentum compounding."],
];

export const BrandFlow = ({ dark = false }) => {
  const [active, setActive] = useState(0);
  return (
    <div data-testid="brand-flow">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-3 sm:gap-x-3">
        {STEPS.map(([label], i) => (
          <div key={label} className="flex items-center gap-2 sm:gap-3">
            <button
              data-testid={`brand-flow-step-${i}`}
              onClick={() => setActive(i)}
              className={`border px-4 py-2.5 font-mono text-[11px] uppercase tracking-[0.15em] transition-colors duration-300 ${
                active === i
                  ? "border-gold bg-gold text-ink"
                  : dark
                    ? "border-sand/20 text-sand/60 hover:border-gold hover:text-gold"
                    : "border-ink/15 text-ink/60 hover:border-gold hover:text-gold-dark"
              }`}
            >
              <span className={active === i ? "text-ink/50" : "text-gold-dark/70"}>
                {String(i + 1).padStart(2, "0")}
              </span>{" "}
              {label}
            </button>
            {i < STEPS.length - 1 && (
              <ArrowRight size={13} className={dark ? "text-gold/40" : "text-gold-dark/40"} />
            )}
          </div>
        ))}
      </div>
      <div
        data-testid="brand-flow-panel"
        className={`mt-8 border-l-2 border-gold py-2 pl-6 ${dark ? "text-sand" : "text-ink"}`}
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={active}
            initial={{ opacity: 0, x: 16 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -10 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          >
            <p className={`font-mono text-[10px] uppercase tracking-[0.3em] ${dark ? "text-gold" : "text-gold-dark"}`}>
              Step {String(active + 1).padStart(2, "0")} — {STEPS[active][0]}
            </p>
            <p className={`mt-2 max-w-xl font-serif text-xl italic leading-relaxed sm:text-2xl ${dark ? "text-sand/70" : "text-smoke"}`}>
              {STEPS[active][1]}
            </p>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};
